import React, { useEffect, useRef } from 'react';
import { textWithUrlsToHtml, getParentTaskChain, MAX_LINK_DISPLAY_LENGTH } from '../../../utils/taskUtils';
import './TaskText.css';

const PLACEHOLDER_DESKTOP = 'Start typing and press \u23CE to create tasks';
const PLACEHOLDER_MOBILE = 'New task ...';

function TaskText({
  task,
  tasks,
  isEditing,
  isComplete,
  hasSubtasks,
  filter,
  isMobile,
  editingElementRef,
  onTaskTextClick,
  onTaskTextFocus,
  onTaskTextInput,
  onTaskTextBlur,
  onTaskTextKeyDown,
  onTaskTextPaste
}) {
  const elementRef = useRef(null);
  const wasEditingRef = useRef(false);

  // Convert between HTML and plain text when entering/exiting edit mode
  // Only applies when not showing parent hierarchy (normal view)
  const showParentHierarchy = filter === 'prioritize' && tasks && task.parent_task_id;
  const parentChain = showParentHierarchy ? getParentTaskChain(task.id, tasks) : [];
  const hasParentHierarchy = showParentHierarchy && parentChain.length > 0;
  
  useEffect(() => {
    if (!elementRef.current || hasParentHierarchy) return;
    const element = elementRef.current;

    if (isEditing && !wasEditingRef.current) {
      // Entering edit mode: convert HTML to plain text
      
      // Check if element has HTML content (not just plain text)
      const hasHtmlContent = element.innerHTML !== element.textContent || 
                            element.querySelector('a, span') !== null;
      
      if (hasHtmlContent) {
        // Convert HTML to plain text
        element.textContent = task.text || '';
      } else if (!element.textContent || element.textContent.trim() === '') {
        // Ensure we have the task text
        element.textContent = task.text || '';
      }
      
      // Handle placeholder - empty = no characters (space alone should hide ::before)
      if (task.id === 'placeholder') {
        const text = element.textContent || '';
        if (text.length > 0) {
          element.classList.remove('task-text-placeholder-empty');
        } else {
          element.classList.add('task-text-placeholder-empty');
        }
      }
      
      editingElementRef.current = element;
      wasEditingRef.current = true;
    } else if (!isEditing && wasEditingRef.current) {
      // Exiting edit mode: convert plain text back to HTML
      const currentText = element.textContent || '';
      
      // Convert to HTML for display (with links)
      if (task.id === 'placeholder' && !task.text) {
        element.innerHTML = '';
      } else {
        const text = task.text || '\u00A0';
        if (text === '\u00A0') {
          element.innerHTML = '\u00A0';
        } else {
          element.innerHTML = textWithUrlsToHtml(text, { maxLinkDisplayLength: MAX_LINK_DISPLAY_LENGTH });
        }
      }
      
      wasEditingRef.current = false;
    } else if (!isEditing && !wasEditingRef.current) {
      // Not editing and wasn't editing: ensure HTML content is set
      // This handles initial render and updates when task.text changes
      const currentHtml = element.innerHTML;
      const expectedHtml = (() => {
        if (task.id === 'placeholder' && !task.text) {
          return '';
        }
        const text = task.text || '\u00A0';
        if (text === '\u00A0') return '\u00A0';
        return textWithUrlsToHtml(text, { maxLinkDisplayLength: MAX_LINK_DISPLAY_LENGTH });
      })();
      
      // Only update if HTML is different (avoid unnecessary updates)
      // Also handle case where element is empty (initial render)
      if (!currentHtml || currentHtml !== expectedHtml) {
        element.innerHTML = expectedHtml;
      }
    }
  }, [isEditing, task.text, task.id, editingElementRef, hasParentHierarchy]);
  
  // Handle editing mode for parent hierarchy view
  useEffect(() => {
    if (!hasParentHierarchy || !elementRef.current) return;
    const element = elementRef.current;

    if (isEditing && !wasEditingRef.current) {
      // Entering edit mode: set plain text
      element.textContent = task.text || '';
      editingElementRef.current = element;
      wasEditingRef.current = true;
    } else if (!isEditing && wasEditingRef.current) {
      // Exiting edit mode: convert to HTML
      const text = task.text || '\u00A0';
      if (text === '\u00A0') {
        element.innerHTML = '\u00A0';
      } else {
        element.innerHTML = textWithUrlsToHtml(text, { maxLinkDisplayLength: MAX_LINK_DISPLAY_LENGTH });
      }
      wasEditingRef.current = false;
    } else if (!isEditing && !wasEditingRef.current) {
      // Ensure HTML content is set
      const text = task.text || '\u00A0';
      const expectedHtml = text === '\u00A0' ? '\u00A0' : textWithUrlsToHtml(text, { maxLinkDisplayLength: MAX_LINK_DISPLAY_LENGTH });
      if (element.innerHTML !== expectedHtml) {
        element.innerHTML = expectedHtml;
      }
    }
  }, [isEditing, task.text, task.id, hasParentHierarchy, editingElementRef]);

  // In prioritize view with parent hierarchy, we need a wrapper structure
  if (hasParentHierarchy) {
    // Get only the immediate parent (last in chain, which is the direct parent)
    const immediateParent = parentChain[parentChain.length - 1];
    const immediateParentText = immediateParent?.text || '';
    
    if (isEditing) {
      // Editing mode: show immediate parent (bold) + editable task text
      return (
        <span className={`task-text ${isComplete ? 'task-text-complete' : ''} ${hasSubtasks ? 'task-text-parent' : ''} ${task.id === 'placeholder' ? 'task-text-placeholder' : ''}`}>
          {immediateParentText && (
            <span className="task-text-immediate-parent">
              {immediateParentText}{' › '}
            </span>
          )}
          <span
            ref={(el) => {
              elementRef.current = el;
              if (el && isEditing) {
                editingElementRef.current = el;
              }
            }}
            className="task-text-content task-text-editing"
            contentEditable={true}
            suppressContentEditableWarning={true}
            autoCapitalize="sentences"
            data-task-id={task.id}
            data-react-key={task._reactKey}
            onFocus={() => onTaskTextFocus(task._reactKey)}
            onInput={(e) => onTaskTextInput && onTaskTextInput(task._reactKey, e)}
            onBlur={(e) => onTaskTextBlur(task._reactKey, e)}
            onKeyDown={(e) => onTaskTextKeyDown(e, task._reactKey)}
            onPaste={(e) => onTaskTextPaste(e, task._reactKey)}
          />
        </span>
      );
    } else {
      // Display mode: show immediate parent (bold) + task text — onMouseDown on outer span so first tap enters edit; inner span onClick for caret when clicking task text
      return (
        <span
          className={`task-text ${isComplete ? 'task-text-complete' : ''} ${hasSubtasks ? 'task-text-parent' : ''} ${task.id === 'placeholder' ? 'task-text-placeholder' : ''}`}
          data-task-id={task.id}
          data-react-key={task._reactKey}
          onMouseDown={(e) => {
            if (e.target.closest('.expand-button, .task-checkbox, .task-due-date, .prioritize-actions, .task-edit-toolbar')) return;
            onTaskTextClick(task._reactKey, e);
          }}
          onClick={(e) => {
            if (!e.target.closest('.task-text-content')) {
              onTaskTextClick(task._reactKey, e);
            }
          }}
        >
          {immediateParentText && (
            <span className="task-text-immediate-parent">
              {immediateParentText}{' › '}
            </span>
          )}
          <span
            ref={(el) => {
              elementRef.current = el;
            }}
            className="task-text-content"
            data-task-id={task.id}
            data-react-key={task._reactKey}
            onClick={(e) => onTaskTextClick(task._reactKey, e)}
            dangerouslySetInnerHTML={{ __html: textWithUrlsToHtml(task.text || '\u00A0', { maxLinkDisplayLength: MAX_LINK_DISPLAY_LENGTH }) }}
          />
        </span>
      );
    }
  }

  // Non-prioritize view or no parent hierarchy: use original structure
  return (
    <span
      ref={(el) => {
        elementRef.current = el;
        if (el && isEditing) {
          editingElementRef.current = el;
        }
      }}
      className={`task-text ${isComplete ? 'task-text-complete' : ''} ${isEditing ? 'task-text-editing' : ''} ${hasSubtasks ? 'task-text-parent' : ''} ${task.id === 'placeholder' ? 'task-text-placeholder' : ''} ${task.id === 'placeholder' && (task.text == null || task.text.length === 0) ? 'task-text-placeholder-empty' : ''}`}
      contentEditable={isEditing}
      suppressContentEditableWarning={isEditing}
      autoCapitalize="sentences"
      data-task-id={task.id}
      data-react-key={task._reactKey}
      data-placeholder={task.id === 'placeholder' ? (isMobile ? PLACEHOLDER_MOBILE : PLACEHOLDER_DESKTOP) : ''}
      onClick={!isEditing ? (e) => onTaskTextClick(task._reactKey, e) : undefined}
      onFocus={() => isEditing && onTaskTextFocus(task._reactKey)}
      onInput={(e) => {
        if (isEditing) {
          if (onTaskTextInput) {
            onTaskTextInput(task._reactKey, e);
          }
          // Handle placeholder styling - empty = no characters (space alone should hide ::before)
          if (task.id === 'placeholder') {
            const text = e.target.textContent || '';
            if (text.length > 0) {
              e.target.classList.remove('task-text-placeholder-empty');
            } else {
              e.target.classList.add('task-text-placeholder-empty');
            }
          }
        }
      }}
      onBlur={(e) => isEditing && onTaskTextBlur(task._reactKey, e)}
      onKeyDown={(e) => isEditing && onTaskTextKeyDown(e, task._reactKey)}
      onPaste={(e) => isEditing && onTaskTextPaste(e, task._reactKey)}
    />
  );
}

export default TaskText;


